import type { ReactNode } from "react";
import { Link } from "react-router";
import { formatReleaseDate, pickText, type BannerProduct, type PublicBanner } from "../../shared/banners";
import { useI18n } from "../i18n/I18nProvider";
import { imageUrl } from "../lib/api";
import { useMoney } from "../lib/CurrencyProvider";

/** A site path goes through the router; an https link opens in a new tab. */
export function BannerLink({ to, className, children, label, tabIndex }: { to: string; className?: string; children: ReactNode; label?: string; tabIndex?: number }) {
  if (to.startsWith("/")) {
    return (
      <Link to={to} className={className} aria-label={label} tabIndex={tabIndex}>
        {children}
      </Link>
    );
  }
  return (
    <a href={to} className={className} aria-label={label} tabIndex={tabIndex} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  );
}

/** Up to three cards fanned out, the first (main) one in the middle. */
function CardFan({ products, showPrice, tabbable }: { products: BannerProduct[]; showPrice: boolean; tabbable: boolean }) {
  const { pick } = useI18n();
  const { format } = useMoney();
  const slots = products.length >= 3 ? [products[1], products[0], products[2]] : products.length === 2 ? [products[1], products[0]] : products;
  const cls = products.length >= 3 ? ["c1", "c2", "c3"] : products.length === 2 ? ["c1", "c2"] : ["c2"];
  return (
    <div className="hero-art">
      {slots.map((p, i) => (
        <Link key={p.id} to={`/producto/${p.slug}`} className={`hero-card ${cls[i]} has-img`} title={`${pick(p.name)} · ${format(p.price)}`} tabIndex={tabbable ? undefined : -1}>
          <img src={imageUrl(p.imageKey)} alt={pick(p.name)} loading={i === 0 ? "eager" : "lazy"} />
          {showPrice && (
            <span className="hero-card-price">
              {p.compareAtPrice != null && p.compareAtPrice > p.price && <s>{format(p.compareAtPrice)}</s>} {format(p.price)}
            </span>
          )}
        </Link>
      ))}
    </div>
  );
}

/**
 * One banner of the home carousel (also the admin preview). `active`: the slide on screen; the
 * others stay out of the tab order.
 */
export function BannerSlide({ banner, active = true, first = false }: { banner: PublicBanner; active?: boolean; first?: boolean }) {
  const { t, locale } = useI18n();
  const title = pickText(banner.title, locale);
  const text = pickText(banner.text, locale);
  const cta = pickText(banner.cta, locale);
  const badge = pickText(banner.badge, locale);
  const tab = active ? undefined : -1;

  if (banner.kind === "image" && banner.imageKey) {
    const img = <img src={imageUrl(banner.imageKey)} alt={pickText(banner.alt, locale) || title} className="banner-image" loading={active ? "eager" : "lazy"} />;
    return (
      <div className={`banner banner-image-slide theme-${banner.theme}`}>
        {banner.link ? (
          <BannerLink to={banner.link} className="banner-image-link" tabIndex={tab}>
            {img}
          </BannerLink>
        ) : (
          img
        )}
        {(title || cta) && (
          <div className="banner-image-caption">
            {title && <h2>{title}</h2>}
            {cta && banner.link && (
              <BannerLink to={banner.link} className="btn btn-accent" tabIndex={tab}>
                {cta}
              </BannerLink>
            )}
          </div>
        )}
      </div>
    );
  }

  const release = banner.kind === "presale" ? formatReleaseDate(banner.releaseDate, locale) : "";
  const kicker = banner.kind === "presale" ? t("banner.presale") : banner.kind === "set" ? banner.setName : null;
  const Heading = first ? "h1" : "h2";
  return (
    <div className={`banner hero theme-${banner.theme} banner-${banner.kind}${banner.bgImageKey ? " has-bg" : ""}`}>
      {banner.bgImageKey && <img src={imageUrl(banner.bgImageKey)} alt="" className="banner-bg" loading={active ? "eager" : "lazy"} />}
      <div className="container hero-inner">
        <div className="banner-copy">
          {(kicker || release) && (
            <p className="banner-kicker">
              {kicker && <span className="banner-chip">{kicker}</span>}
              {release && <span>{t("banner.releases", { date: release })}</span>}
            </p>
          )}
          {/* The first slide's title is the page's main heading; the rest are h2. */}
          <Heading className="banner-title">{title}</Heading>
          {text && <p>{text}</p>}
          {cta && banner.link && (
            <BannerLink to={banner.link} className="btn btn-accent btn-lg" tabIndex={tab}>
              {cta}
            </BannerLink>
          )}
        </div>
        <div className="banner-art">
          {banner.kind === "promo" && badge && <span className="banner-sticker">{badge}</span>}
          {banner.products.length > 0 ? (
            <CardFan products={banner.products} showPrice={banner.kind === "promo" || banner.kind === "presale"} tabbable={active} />
          ) : banner.imageKey ? (
            <img src={imageUrl(banner.imageKey)} alt="" className="banner-side-image" />
          ) : null}
        </div>
      </div>
    </div>
  );
}
